import React, { useContext } from 'react'

import { AppIconSizes, AppIconVariants, HR, Settings } from '@inno-ui/app-icons'
import { Avatar, AvatarSizes } from '@inno-ui/avatar'
import { Button, ButtonVariants } from '@inno-ui/button'
import {
  ArrowDownIcon,
  ArrowRightRectangleFullIcon,
  FlagFullIcon,
  IconSizes,
  RocketFullIcon,
  ShoppingCartFullIcon
} from '@inno-ui/icons'
import { useLogout } from 'modules/auth/hooks/useLogout'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { CustomEvents } from 'shared/constants/customEvents'
import { UserContext } from 'shared/contexts'
import { useUserCompanies } from 'shared/hooks/useUserCompanies'
import { dispatchCustomEvent } from 'shared/utils/customEvents'

import { FreeTrialSection } from '../FreeTrialSection/FreeTrialSection'
import { LanguageSection } from '../LanguageSection'
import { OrganizationSection } from '../OrganizationSection'
import { ProfileMenuRow } from '../ProfileMenuRow/ProfileMenuRow'
import styles from './HomeLayer.module.scss'
import type { HomeLayerProps } from './HomeLayer.types'

export const HomeLayer: React.FC<HomeLayerProps> = ({
  editPreferencesButtonRef,
  onEditProfileClick,
  onProductTourClick,
  onMenuItemClick
}) => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const { selectedCompany, companies } = useUserCompanies()
  const logout = useLogout()

  const openReleaseNotes = () => {
    onMenuItemClick()
    dispatchCustomEvent(CustomEvents.OPEN_RELEASE_NOTES)
  }

  return (
    <div className={styles.home}>
      <section className={styles.userInfo}>
        <div className={styles.avatar}>
          <Avatar
            size={AvatarSizes.Size120}
            url={user?.profile?.avatar ?? ''}
            username={user?.name}
          />
        </div>
        <div className={styles.details}>
          <div className={styles.identity}>
            <p className={styles.userName}>
              {user?.name ?? t('navbar.userMenu.userPlaceholder')}
            </p>
            <p className={styles.email}>{user?.email || '-'}</p>
          </div>
          <Button
            ref={editPreferencesButtonRef}
            variant={ButtonVariants.Text}
            label={t('navbar.userMenu.editPreferences')}
            onClick={onEditProfileClick}
          />
        </div>
      </section>
      <div className={styles.content}>
        <FreeTrialSection />
        <div className={styles.menu}>
          <OrganizationSection
            companies={companies}
            selectedCompany={selectedCompany}
          />
          <LanguageSection />
          <div className={styles.separator} />
          <ProfileMenuRow
            label={t('navbar.userMenu.whatsNew')}
            icon={
              <RocketFullIcon
                className={styles.mutedIcon}
                size={IconSizes.Size16}
                color='currentColor'
              />
            }
            onClick={openReleaseNotes}
          />
          <ProfileMenuRow
            label={t('navbar.userMenu.productTour')}
            icon={
              <FlagFullIcon
                className={styles.mutedIcon}
                size={IconSizes.Size16}
                color='currentColor'
              />
            }
            onClick={onProductTourClick}
          />
          <ProfileMenuRow
            label={t('navbar.userMenu.downloadCenter')}
            icon={
              <span className={styles.downloadIcon} aria-hidden='true'>
                <ShoppingCartFullIcon
                  size={IconSizes.Size16}
                  color='currentColor'
                />
                <span className={styles.downloadArrow}>
                  <ArrowDownIcon size={IconSizes.Size10} color='currentColor' />
                </span>
              </span>
            }
            href='/download'
            onClick={onMenuItemClick}
          />
          <div className={styles.separator} />
          <ProfileMenuRow
            label={t('navbar.userMenu.logout')}
            icon={
              <ArrowRightRectangleFullIcon
                className={styles.mutedIcon}
                size={IconSizes.Size16}
                color='currentColor'
              />
            }
            onClick={() => {
              onMenuItemClick()
              logout('logout_button_clicked_HomeLayer')
            }}
          />
        </div>
        <div className={styles.tiles}>
          <Link to='/profile' onClick={onMenuItemClick} className={styles.tile}>
            <span className={styles.tileIcon}>
              <HR
                size={AppIconSizes.Size34}
                variant={AppIconVariants.Colored}
              />
            </span>
            {t('user_profile.my_profile')}
          </Link>
          <Link
            to='/settings'
            onClick={onMenuItemClick}
            className={styles.tile}
          >
            <span className={styles.tileIcon}>
              <Settings
                size={AppIconSizes.Size34}
                variant={AppIconVariants.Colored}
              />
            </span>
            {t('navbar.userMenu.settings')}
          </Link>
        </div>
      </div>
    </div>
  )
}
